import {Draggable, gsap, InertiaPlugin} from "gsap/all";
import $ from "jquery";
import {FaceLandmarker, FilesetResolver} from "@mediapipe/tasks-vision";

export class LevelWithCamera {

  constructor(cameraFeed, targetCanvas, visionEngine) {
    this.faceLandmarker = visionEngine;
    this.sharedCameraFeed = cameraFeed;
    this.cameraCanvasEl = targetCanvas;
    this.cameraCanvasElCtx = this.cameraCanvasEl.getContext('2d');
    this.cameraPlaying = false;
    this.latestLandmarks = [];

    this.partsIndexes = {
      FOREHEAD: 10,
      NOSE_TIP: 1,
      MOUTH_UP: 13,
      MOUTH_DOWN: 14,
      CHIN: 199,
      RIGHT_EYE: 470, // iris
      LEFT_EYE: 475, // iris
      LEFT_EAR: 454,
      RIGHT_IRIS: this.getPartIndexes(FaceLandmarker.FACE_LANDMARKS_RIGHT_IRIS),
      LEFT_IRIS: this.getPartIndexes(FaceLandmarker.FACE_LANDMARKS_LEFT_IRIS),
      RIGHT_EYE_CONTOUR: this.getPartIndexes(FaceLandmarker.FACE_LANDMARKS_RIGHT_EYE),
      LEFT_EYE_CONTOUR: this.getPartIndexes(FaceLandmarker.FACE_LANDMARKS_LEFT_EYE),
      MOUTH_CONTOUR: this.getPartIndexes(FaceLandmarker.FACE_LANDMARKS_LIPS),

      // RIGHT_EYE: [33, 133, 160, 159, 158, 157, 173, 144, 145, 153, 154, 155], // we will use a set of points
      // LEFT_EAR: null, // the point doesn't exist so we'll compute it
    }

    // console.log("constructor level with cam")
  }

  init() {
    // console.log("init level with camera");
  }

  getPartIndexes(faceLandmarkerIndexes) {
    return faceLandmarkerIndexes.map(index => index.start);
  }

  //
  // detectFace() {
  //   const detection = this.faceLandmarker.detectForVideo(this.cameraCanvasEl, performance.now());
  //
  //   if (detection.faceLandmarks?.length) {
  //     // We found a face, so let's return the landmarks
  //     return detection.faceLandmarks[0];
  //   } else {
  //     // No face detected, return empty
  //     return [];
  //   }
  // }

  updateFaceLandmarks() {
    // Now the face detection is handled all the time in the SharedCamera so here's a simple retrofit to get the points from there instead of computing it ourselves
    // this.latestLandmarks = this.detectFace();
    this.latestLandmarks = this.sharedCameraFeed.scaledlatestLandmarks;
  }


  getMainFaceLandmarksPoints() {

    // Here we will return the main 6 points for the face which are:
    // Forehead, Nose tip, Mouth center, Left ear, Right eye and the Chin
    // If there's no current detection, let's update the points now
    if (this.latestLandmarks.length == 0) {
      this.updateFaceLandmarks();
    }

    const landmarks = this.latestLandmarks;

    if (landmarks.length === 0) {
      return [];
    }

    return [
      {name: "forehead", ...landmarks[this.partsIndexes["FOREHEAD"]]},
      {name: "nose", ...landmarks[this.partsIndexes["NOSE_TIP"]]},
      {name: "mouth", ...this.getMouthCenter(landmarks, this.partsIndexes)},
      {name: "chin", ...landmarks[this.partsIndexes["CHIN"]]},
      {name: "eye", ...this.getEyeCenter(landmarks, this.partsIndexes["RIGHT_IRIS"])},
      {name: "left_eye", ...this.getEyeCenter(landmarks, this.partsIndexes["LEFT_IRIS"])},
      {name: "ear", ...landmarks[this.partsIndexes["LEFT_EAR"]]},
    ];
  }

  getLeftEarPoint(landmarks) {
    // We can use the left most point to get the left ear
    return landmarks.reduce((a, b) => a.x < b.x ? a : b);
  }

  getMouthCenter(landmarks, partsIndexes) {
    return {
      x: (landmarks[partsIndexes.MOUTH_UP].x + landmarks[partsIndexes.MOUTH_DOWN].x) / 2,
      y: (landmarks[partsIndexes.MOUTH_UP].y + landmarks[partsIndexes.MOUTH_DOWN].y) / 2,
      z: (landmarks[partsIndexes.MOUTH_UP].z + landmarks[partsIndexes.MOUTH_DOWN].z) / 2
    };
  }

  getEyeCenter(landmarks, indexes) {
    return this.averagePoint(indexes.map(i => landmarks[i]))
  }

  averagePoint(points) {
    // Returns the average point for a set of points
    let sx = 0, sy = 0, sz = 0;
    for (const p of points) {
      sx += p.x;
      sy += p.y;
      sz += p.z;
    }
    return {x: sx / points.length, y: sy / points.length, z: sz / points.length};
  }

  isFaceCorrectSize() {
    let faceBoundingBox = this.sharedCameraFeed.latestScaledFaceBoundingBox;

    // We want the face to have a width at least X% the width of the window and not more than Y% so it fits correctly in the circle
    // We want the face to have a height NOT MORE than X% of the window so it fits correctly in the circle. There's no need to check for min height because width would be too small anyway
    if (
      faceBoundingBox.w >= (this.sharedCameraFeed.height * app.settings.webcam.face_requirements.dimensions.min_width) &&
      faceBoundingBox.w <= (this.sharedCameraFeed.height * app.settings.webcam.face_requirements.dimensions.max_width) &&
      faceBoundingBox.h < (this.sharedCameraFeed.width * app.settings.webcam.face_requirements.dimensions.max_height)
    ) {
      return true;
    }

    return false;
  }

  getFaceWidthToScreenRatio() {
    let faceBoundingBox = this.sharedCameraFeed.latestScaledFaceBoundingBox;

    return faceBoundingBox.w / this.sharedCameraFeed.height; // width of the face box on screen / screen width
  }

  isFaceCorrectlyCentered() {
    let faceBoundingBox = this.sharedCameraFeed.latestScaledFaceBoundingBox;

    let faceCenter = {
      x: (faceBoundingBox.x + (faceBoundingBox.w / 2)),
      y: (faceBoundingBox.y + (faceBoundingBox.h / 2))
    }

    // We want the face center to be within a set of % of the width/height of the screen so it is correctly centered
    if (
      faceCenter.x >= (this.sharedCameraFeed.height * app.settings.webcam.face_requirements.bounding_box.min_x) &&
      faceCenter.x <= (this.sharedCameraFeed.height * app.settings.webcam.face_requirements.bounding_box.max_x) &&
      faceCenter.y >= (this.sharedCameraFeed.width * app.settings.webcam.face_requirements.bounding_box.min_y) &&
      faceCenter.y <= (this.sharedCameraFeed.width * app.settings.webcam.face_requirements.bounding_box.max_y)
    ) {
      return true;
    }

    return false;
  }

  drawCamera() {
    if (!this.cameraPlaying) {
      // Camera has been stopped so let's stop rendering
      return;
    }

    // Get the image from the shared camera and draw it on our canvas
    this.cameraCanvasElCtx.clearRect(0, 0, this.cameraCanvasEl.width, this.cameraCanvasEl.height);
    this.cameraCanvasElCtx.drawImage(this.sharedCameraFeed.getCanvas(), 0, 0);

    // Now that we got a feed, we can call a function that can be overwritten by the level to add overlays to the image
    this.drawCameraOverlays();

    // =========== ON SITE DEBUGGING ===========
    // =========== UNCOMMENT FOR EASIER FACE POSITION/SIZE DEBUGGING ===========
    // let faceBoundingBox = this.sharedCameraFeed.latestScaledFaceBoundingBox;
    // if(faceBoundingBox){
    //   let faceCenter = {
    //     x: (faceBoundingBox.x + (faceBoundingBox.w / 2)),
    //     y: (faceBoundingBox.y + (faceBoundingBox.h / 2))
    //   }
    //
    //   let arr = {
    //     "Face width": faceBoundingBox.w / this.sharedCameraFeed.height,
    //     "Face height": faceBoundingBox.h / this.sharedCameraFeed.width,
    //     "Face correct size?": this.isFaceCorrectSize(),
    //     "Face width less than min_width? (dimensions.min_width)": !(faceBoundingBox.w >= (this.sharedCameraFeed.height * app.settings.webcam.face_requirements.dimensions.min_width)),
    //     "Face width more than max_width? (dimensions.max_width)": !(faceBoundingBox.w <= (this.sharedCameraFeed.height * app.settings.webcam.face_requirements.dimensions.max_width)),
    //     "Face height more than max_height? (dimensions.max_heigh)": !(faceBoundingBox.h < (this.sharedCameraFeed.width * app.settings.webcam.face_requirements.dimensions.max_height)),
    //     "Face is correctly centered?": this.isFaceCorrectlyCentered(),
    //     "Face is to much to the left? (bounding_box.min_x)": !(faceCenter.x >= (this.sharedCameraFeed.height * app.settings.webcam.face_requirements.bounding_box.min_x)),
    //     "Face is to much to the right? (bounding_box.max_x)": !(faceCenter.x <= (this.sharedCameraFeed.height * app.settings.webcam.face_requirements.bounding_box.max_x)),
    //     "Face is to much to the top? (bounding_box.min_y)": !(faceCenter.y >= (this.sharedCameraFeed.width * app.settings.webcam.face_requirements.bounding_box.min_y)),
    //     "Face is to much to the bottom? (bounding_box.max_y)": !(faceCenter.y <= (this.sharedCameraFeed.width * app.settings.webcam.face_requirements.bounding_box.max_y)),
    //   }
    //   console.table(arr);
    // }


    requestAnimationFrame(() => {
      this.drawCamera()
    });
  }

  getCameraCanvas() {
    return this.cameraCanvasEl;
  }

  drawCameraOverlays() {
    // To be overwritten in child
  }

  startCamera() {
    if (!this.cameraPlaying) {
      this.cameraPlaying = true;
      this.drawCamera();
    }
  }

  pauseCamera() {
    if (this.cameraPlaying) {
      this.cameraPlaying = false;
    }
  }

  startMainCamera() {
    this.sharedCameraFeed.playMainCamera();
  }

  pauseMainCamera() {
    this.sharedCameraFeed.pauseMainCamera();
  }

  resetLandmarks() {
    this.latestLandmarks = [];
  }

  reset() {
    // TODO
    // console.log("RESET FROM LEVEL WITH CAM");
    this.resetLandmarks();
    this.pauseCamera();
    this.startMainCamera(); // make sure the main camera is working
    this.sharedCameraFeed.reset();
  }

}
